import { clientDetailsBreadcrumb } from "../clients/client-breadcrumb";

/**
 * Interface defining the parameters for building breadcrumb functions.
 * 
 * @interface IParams
 * @property {Object} client - Client information
 * @property {string} [client.name] - Name of the client
 * @property {number} client.id - ID of the client
 * @property {Object} [building] - Building information (optional)
 * @property {string} [building.name] - Name of the building
 * @property {number} [building.id] - ID of the building
 */
export interface IParams {
  client: { name?: string; id: number };
  building?: { name?: string; id: number };
}

/**
 * Generates breadcrumb navigation for building details view.
 * Creates a hierarchical path showing the client and building context.
 * 
 * @function buildingDetailsBreadcrumb
 * @param {IParams} props - Parameters containing client and building information
 * @returns {Array<{title: string, path: string, disabledPath?: boolean}>} Array of breadcrumb items
 * 
 * @example
 * ```tsx
 * const breadcrumbs = buildingDetailsBreadcrumb({
 *   client: { name: "Client Name", id: 1 },
 *   building: { name: "Building Name", id: 2 }
 * });
 * ```
 */
export const buildingDetailsBreadcrumb = (props: IParams) => {
  const { client, building } = props;

  const clientDetails = clientDetailsBreadcrumb({
    client: { name: client?.name, id: client?.id },
  });

  const buildingDetails = [
    ...clientDetails,
    { title: "Gebäude", path: `/clients/${client.id}` },
    {
      title: building?.name ? building?.name : "Loading...",
      path: `/clients/${client.id}/buildings/${building?.id}`,
      disabledPath: true,
    },
  ];

  return buildingDetails.filter((item) => item.title !== "");
};

/**
 * Generates breadcrumb navigation for building creation view.
 * Creates a path showing the client context and indicating new building creation.
 * 
 * @function creatbuildingBreadcrumb
 * @param {IParams} props - Parameters containing client information
 * @returns {Array<{title: string, path: string, disabledPath?: boolean}>} Array of breadcrumb items
 * 
 * @example
 * ```tsx
 * const breadcrumbs = creatbuildingBreadcrumb({
 *   client: { name: "Client Name", id: 1 }
 * });
 * ```
 */
export const creatbuildingBreadcrumb = (props: IParams) => {
  const { client } = props;

  return [
    { title: "Home", path: "/" },
    { title: "Kunden", path: "/clients" },
    {
      title: client?.name ? client?.name : "Loading...",
      path: `/clients/${client.id}`,
    },
    {
      title: "Gebäude",
      path: `/clients/${client.id}`,
      disabledPath: true,
    },
  ].filter((item) => item.title);
};
